/* eslint-disable react-hooks/exhaustive-deps */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { MessageCircle, Send, Smile, ImagePlay, Paperclip, X, MoreVertical, Edit3, Trash2, Loader2, ArrowLeft, Plus, Shield, Users, Mail, MessageSquare, Bold, Italic, Underline, Code, Link as LinkIcon, CornerUpLeft, ExternalLink, Minimize2, ListTree } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import {
  listChatThreads, listPublicChat, postPublicChat,
  listChatMessages, postChatMessage,
  editChatMessage, deleteChatMessage, uploadChatFile, uploadChatGif,
  createDm, createGroupChat, toggleChatMessageReaction,
} from "../lib/api";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "");

const QUICK_EMOJIS = ["👏", "🔥", "🎉", "💯", "❤️", "😂", "🎶", "✨", "🙌", "👀", "😭", "🤘"];

// ---------- helpers ----------
function formatTime(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}
function avatarColor(identity) {
  let h = 0; for (let i = 0; i < (identity || "").length; i++) h = (h * 31 + identity.charCodeAt(i)) & 0xffff;
  return `hsl(${h % 360}, 70%, 50%)`;
}
function fileUrl(u) {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("blob:")) return u;
  return API_BASE + u;
}

// ---------- emoji picker ----------
function EmojiPickerLazy({ onPick, onClose }) {
  const [Picker, setPicker] = useState(null);
  useEffect(() => { import("emoji-picker-react").then((m) => setPicker(() => m.default)); }, []);
  if (!Picker) return <div className="p-3 text-xs text-white/60">Loading…</div>;
  return (
    <div className="rounded-lg overflow-hidden border border-white/10 shadow-2xl" data-testid="chat-emoji-picker-pop">
      <Picker onEmojiClick={(e) => { onPick(e.emoji); onClose?.(); }} theme="dark" lazyLoadEmojis searchDisabled={false} width={320} height={380} />
    </div>
  );
}

// ---------- emoji picker ----------
function MessageRow({ m, myIdentity, canModerate, onEdit, onDelete, onReply, onReact }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const isMine = m.author_identity === myIdentity;
  const body = m.body || {};
  const isSystem = m.author_identity === "system" || body.kind === "system";

  if (isSystem) {
    return (
      <div className="px-3 py-1.5" data-testid={`chat-msg-${m.message_id}`}>
        <div className="text-[11px] text-white/50 italic border-l-2 border-[#00F0FF]/40 pl-2 py-0.5 bg-white/[0.03] rounded-r" data-testid={`chat-msg-system-${m.message_id}`}>
          {body.text || "(system event)"} <span className="text-white/30 not-italic ml-1 font-mono">{formatTime(m.created_at)}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="group flex items-start gap-2 px-3 py-1.5 hover:bg-white/[0.03] relative" data-testid={`chat-msg-${m.message_id}`}>
      <div className="w-7 h-7 rounded-full shrink-0 grid place-items-center text-[10px] font-bold uppercase"
           style={{ background: avatarColor(m.author_identity) }}>
        {(m.author_name || "?").slice(0, 1)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-0.5">
          <span className="text-[12px] font-semibold text-white/90" data-testid={`chat-msg-author-${m.message_id}`}>{m.author_name || m.author_identity?.slice(0, 8)}</span>
          <span className="text-[10px] text-white/30 font-mono">{formatTime(m.created_at)}</span>
          {m.edited_at && <span className="text-[10px] text-white/30 italic" data-testid={`chat-msg-edited-${m.message_id}`}>(edited)</span>}
        </div>
        {m.deleted || body.kind === "deleted" ? (
          <div className="text-[12px] text-white/30 italic" data-testid={`chat-msg-deleted-${m.message_id}`}>[deleted]</div>
        ) : body.kind === "text" ? (
          <>
            {body.reply_to && (
              <button onClick={() => {
                const el = document.querySelector(`[data-testid="chat-msg-${body.reply_to.message_id}"]`);
                if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("sr-reply-flash"); setTimeout(() => el.classList.remove("sr-reply-flash"), 1500); }
              }} className="block w-full text-left mb-1 pl-2 border-l-2 border-[#00F0FF]/50 text-[11px] text-white/55 hover:text-white/80" data-testid={`chat-msg-reply-ref-${m.message_id}`}>
                <span className="text-[#00F0FF]/80 font-semibold">↩ {body.reply_to.author}</span>
                <span className="ml-1 truncate inline-block max-w-[200px] align-bottom">{body.reply_to.preview}</span>
              </button>
            )}
            <div className="text-[13px] text-white/85 leading-snug chat-md prose prose-invert prose-sm max-w-none" data-testid={`chat-msg-text-${m.message_id}`}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                allowedElements={["p", "em", "strong", "del", "code", "ul", "ol", "li", "a", "br", "pre"]}
                unwrapDisallowed
                components={{ a: ({node, ...p}) => <a {...p} target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] underline" /> }}
              >{body.text}</ReactMarkdown>
            </div>
          </>
        ) : body.kind === "gif" || body.kind === "image" ? (
          <>
            {body.reply_to && (
              <button onClick={() => {
                const el = document.querySelector(`[data-testid="chat-msg-${body.reply_to.message_id}"]`);
                if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("sr-reply-flash"); setTimeout(() => el.classList.remove("sr-reply-flash"), 1500); }
              }} className="block w-full text-left mb-1 pl-2 border-l-2 border-[#00F0FF]/50 text-[11px] text-white/55 hover:text-white/80" data-testid={`chat-msg-reply-ref-${m.message_id}`}>
                <span className="text-[#00F0FF]/80 font-semibold">↩ {body.reply_to.author}</span>
                <span className="ml-1 truncate inline-block max-w-[200px] align-bottom">{body.reply_to.preview}</span>
              </button>
            )}
            <a href={fileUrl(body.url)} target="_blank" rel="noopener noreferrer">
            <img src={fileUrl(body.preview_url || body.url)} alt={body.alt || "image"} className="max-w-[240px] rounded-lg border border-white/10" data-testid={`chat-msg-image-${m.message_id}`} />
            </a>
          </>
        ) : body.kind === "file" ? (
          <a href={fileUrl(body.url)} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 px-2 py-1.5 bg-white/5 border border-white/10 rounded text-[12px] hover:bg-white/10"
             data-testid={`chat-msg-file-${m.message_id}`}>
            <Paperclip className="w-3 h-3" /> {body.original_name || "file"} <span className="text-white/40">({Math.round((body.size_bytes||0)/1024)} KB)</span>
          </a>
        ) : (
          <div className="text-[12px] text-white/50 italic">[{body.kind || "unknown"}]</div>
        )}
        {!m.deleted && (m.reactions || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`chat-msg-reactions-${m.message_id}`}>
            {(m.reactions || []).map((reaction) => {
              const reacted = (reaction.identities || []).includes(myIdentity);
              return (
                <button key={reaction.emoji} onClick={() => onReact?.(m, reaction.emoji)}
                        className={`px-1.5 py-0.5 rounded-full text-[11px] border transition-colors ${reacted ? "bg-[#00F0FF]/15 border-[#00F0FF]/45 text-white" : "bg-white/5 border-white/10 text-white/75 hover:bg-white/10"}`}
                        title={`${(reaction.identities || []).length} reaction${(reaction.identities || []).length === 1 ? "" : "s"}`}
                        data-testid={`chat-msg-reaction-${m.message_id}-${reaction.emoji}`}>
                  {reaction.emoji} <span className="ml-0.5">{(reaction.identities || []).length}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {onReact && !m.deleted && (
        <div className="relative self-center shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity">
          <button onClick={() => setShowReactions((open) => !open)}
                  className="p-1 rounded hover:bg-white/10 text-white/50 hover:text-white"
                  title="React to message" aria-label="React to message"
                  data-testid={`chat-msg-react-btn-${m.message_id}`}>
            <Smile className="w-3.5 h-3.5" />
          </button>
          {showReactions && (
            <div className="absolute right-0 top-7 z-20 flex gap-1 p-1.5 rounded-lg bg-[#12121a] border border-white/10 shadow-2xl" data-testid={`chat-msg-react-picker-${m.message_id}`}>
              {QUICK_EMOJIS.slice(0, 8).map((emoji) => (
                <button key={emoji} onClick={() => { setShowReactions(false); onReact(m, emoji); }}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-white/10 text-base"
                        data-testid={`chat-msg-react-pick-${m.message_id}-${emoji}`}>{emoji}</button>
              ))}
            </div>
          )}
        </div>
      )}
      {(isMine || canModerate) && !m.deleted && (
        <button onClick={() => setMenuOpen((v) => !v)}
                className="opacity-0 group-hover:opacity-100 absolute right-2 top-1.5 p-1 rounded hover:bg-white/10"
                data-testid={`chat-msg-menu-${m.message_id}`}>
          <MoreVertical className="w-3.5 h-3.5 text-white/50" />
        </button>
      )}
      {menuOpen && (
        <div className="absolute right-2 top-7 z-10 bg-[#1a1a22] border border-white/10 rounded-lg shadow-2xl py-1 min-w-[120px]" data-testid={`chat-msg-menu-pop-${m.message_id}`}>
          {isMine && body.kind === "text" && (
            <button onClick={() => { setMenuOpen(false); onEdit?.(m); }} className="w-full px-3 py-1.5 text-left text-[12px] hover:bg-white/5 flex items-center gap-2" data-testid={`chat-msg-edit-btn-${m.message_id}`}>
              <Edit3 className="w-3 h-3" /> Edit
            </button>
          )}
          {(isMine || canModerate) && (
            <button onClick={() => { setMenuOpen(false); onDelete?.(m); }} className="w-full px-3 py-1.5 text-left text-[12px] text-rose-300 hover:bg-rose-500/10 flex items-center gap-2" data-testid={`chat-msg-delete-btn-${m.message_id}`}>
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          )}
          {onReply && body.kind !== "deleted" && (
            <button onClick={() => { setMenuOpen(false); onReply?.(m); }} className="w-full px-3 py-1.5 text-left text-[12px] hover:bg-white/5 flex items-center gap-2" data-testid={`chat-msg-reply-btn-${m.message_id}`}>
              <CornerUpLeft className="w-3 h-3" /> Reply
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function MessageBranch({ m, depth, myIdentity, canModerate, onEdit, onDelete, onReply, onReact }) {
  const safeDepth = Math.min(depth, 4);
  return (
    <div className={safeDepth > 0 ? "ml-3 sm:ml-5 border-l border-[#00F0FF]/20" : ""} data-testid={`chat-thread-branch-${m.message_id}`}>
      <MessageRow m={m} myIdentity={myIdentity} canModerate={canModerate}
        onDelete={onDelete} onEdit={onEdit} onReply={onReply} onReact={onReact} />
      {(m.replies || []).map((reply) => (
        <MessageBranch key={reply.message_id} m={reply} depth={safeDepth + 1}
          myIdentity={myIdentity} canModerate={canModerate}
          onDelete={onDelete} onEdit={onEdit} onReply={onReply} onReact={onReact} />
      ))}
    </div>
  );
}

// ---------- thread message view + composer ----------
function ThreadView({ roomId, thread, myIdentity, canModerate, registerHandler }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [editing, setEditing] = useState(null);
  const [replying, setReplying] = useState(null);
  const [linkPopup, setLinkPopup] = useState(null); // {selStart, selEnd, text, url}
  const [pendingAttachment, setPendingAttachment] = useState(null); // { file, kind, previewUrl }
  const threadViewKey = `fsc-chat-view-${roomId}-${thread?.thread_id || "public"}`;
  const [threadedView, setThreadedView] = useState(() => {
    try { return localStorage.getItem(threadViewKey) === "threaded"; } catch { return false; }
  });
  const listEndRef = useRef(null);
  const inputRef = useRef(null);
  const attachmentInputRef = useRef(null);

  // BroadcastChannel — sync state between main window and pop-out
  const bcRef = useRef(null);
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const bc = new BroadcastChannel(`stackroom-chat-${roomId}`);
    bcRef.current = bc;
    bc.onmessage = (ev) => {
      const msg = ev.data;
      if (!msg || msg.thread_id !== thread?.thread_id) return;
      if (msg.type === "bc_new") setMessages((prev) => prev.some((m) => m.message_id === msg.message?.message_id) ? prev : [...prev, msg.message]);
      else if (msg.type === "bc_edit") setMessages((prev) => prev.map((m) => m.message_id === msg.message?.message_id ? msg.message : m));
      else if (msg.type === "bc_delete") setMessages((prev) => prev.map((m) => m.message_id === msg.message_id ? { ...m, deleted: true, body: { kind: "deleted" } } : m));
      else if (msg.type === "bc_reactions") setMessages((prev) => prev.map((m) => m.message_id === msg.message?.message_id ? msg.message : m));
    };
    return () => bc.close();
  }, [roomId, thread?.thread_id]);

  const bcEcho = useCallback((payload) => {
    try { bcRef.current?.postMessage({ ...payload, thread_id: thread?.thread_id }); } catch { /* */ }
  }, [thread?.thread_id]);

  // Render a local pending message immediately. The authoritative server
  // response replaces it, while the LiveKit broadcast remains the reliable
  // delivery path for everyone else in the room.
  const addPendingMessage = useCallback((body) => {
    const message_id = `pending_${crypto.randomUUID?.() || `${Date.now()}_${Math.random()}`}`;
    const draft = {
      message_id,
      thread_id: thread?.thread_id,
      room_id: roomId,
      channel: thread?.kind,
      author_identity: myIdentity,
      author_name: "You",
      body,
      created_at: new Date().toISOString(),
      edited_at: null,
      edited_count: 0,
      deleted: false,
      pending: true,
    };
    setMessages((prev) => [...prev, draft]);
    return message_id;
  }, [myIdentity, roomId, thread?.kind, thread?.thread_id]);

  const resolvePendingMessage = useCallback((pendingId, message) => {
    setMessages((prev) => {
      const withoutPending = prev.filter((m) => m.message_id !== pendingId);
      return withoutPending.some((m) => m.message_id === message.message_id) ? withoutPending : [...withoutPending, message];
    });
  }, []);

  const discardPendingMessage = useCallback((pendingId) => {
    setMessages((prev) => prev.filter((m) => m.message_id !== pendingId));
  }, []);

  // Formatting toolbar — wraps the current selection or inserts placeholder.
  const applyWrap = (left, right = left, placeholder = "text") => {
    const ta = inputRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? input.length;
    const end = ta.selectionEnd ?? input.length;
    const sel = input.slice(start, end) || placeholder;
    const next = input.slice(0, start) + left + sel + right + input.slice(end);
    setInput(next);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + left.length, start + left.length + sel.length); }, 0);
  };
  const insertLink = (text, url) => {
    const ta = inputRef.current;
    const start = ta?.selectionStart ?? input.length;
    const end = ta?.selectionEnd ?? input.length;
    const md = `[${text || "link"}](${url})`;
    setInput(input.slice(0, start) + md + input.slice(end));
    setTimeout(() => { ta?.focus(); ta?.setSelectionRange(start + md.length, start + md.length); }, 0);
  };

  useEffect(() => {
    try { setThreadedView(localStorage.getItem(threadViewKey) === "threaded"); } catch { setThreadedView(false); }
  }, [threadViewKey]);

  const setConversationView = (nextThreaded) => {
    setThreadedView(nextThreaded);
    try { localStorage.setItem(threadViewKey, nextThreaded ? "threaded" : "flat"); } catch { /* */ }
  };

  const messageTree = useMemo(() => {
    const byId = new Map(messages.map((m) => [m.message_id, { ...m, replies: [] }]));
    const roots = [];
    byId.forEach((message) => {
      const parentId = message.body?.reply_to?.message_id;
      const parent = parentId ? byId.get(parentId) : null;
      if (parent && parent.message_id !== message.message_id) parent.replies.push(message);
      else roots.push(message);
    });
    const sort = (items) => {
      items.sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
      items.forEach((item) => sort(item.replies));
    };
    sort(roots);
    return roots;
  }, [messages]);

  // Load history when thread changes
  useEffect(() => {
    if (!thread) return;
    (async () => {
      try {
        if (thread.kind === "public") {
          const r = await listPublicChat(roomId);
          setMessages(r.messages || []);
        } else {
          const r = await listChatMessages(roomId, thread.thread_id);
          setMessages(r.messages || []);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load messages");
      }
    })();
  }, [thread?.thread_id, roomId]);

  // Listen for live updates targeting this thread
  useEffect(() => {
    if (!registerHandler || !thread) return;
    const unsub = registerHandler((msg) => {
      if (!msg) return;
      const tid = msg.thread_id || msg.message?.thread_id;
      if (msg.type === "chat") {
        // Public messages may arrive before the thread list returns its generated
        // public thread id. Public channel membership is sufficient in that case.
        if (thread.kind !== "public" && tid && tid !== thread.thread_id) return;
        if (!tid && thread.kind !== msg.channel) return;
        setMessages((prev) => {
          if (prev.some((m) => m.message_id === msg.message?.message_id)) return prev;
          return [...prev, msg.message];
        });
        // Phase 3.2 — relay incoming LK chat to the pop-out window via BroadcastChannel,
        // so the pop-out (which doesn't connect to LK) still sees messages from others.
        bcEcho({ type: "bc_new", message: msg.message });
      } else if (msg.type === "chat_edit") {
        if (thread.kind !== "public" && tid && tid !== thread.thread_id) return;
        setMessages((prev) => prev.map((m) => m.message_id === msg.message?.message_id ? msg.message : m));
        bcEcho({ type: "bc_edit", message: msg.message });
      } else if (msg.type === "chat_delete") {
        if (thread.kind !== "public" && tid && tid !== thread.thread_id) return;
        setMessages((prev) => prev.map((m) => m.message_id === msg.message_id ? { ...m, deleted: true, body: { kind: "deleted" } } : m));
        bcEcho({ type: "bc_delete", message_id: msg.message_id });
      } else if (msg.type === "chat_reactions") {
        if (thread.kind !== "public" && tid && tid !== thread.thread_id) return;
        setMessages((prev) => prev.map((m) => m.message_id === msg.message?.message_id ? msg.message : m));
        bcEcho({ type: "bc_reactions", message: msg.message });
      }
    });
    return unsub;
  }, [registerHandler, thread?.thread_id, bcEcho]);

  useEffect(() => { listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages.length]);

  // Phase 3.3 — smart auto-scroll: only follow new messages if the user is
  // already near the bottom (within 120px). Otherwise show a "↓ New messages"
  // pill that the user can click to jump down.
  const scrollRootRef = useRef(null);
  const [hasNewBelow, setHasNewBelow] = useState(false);
  const lastSeenCountRef = useRef(0);
  useEffect(() => {
    const el = listEndRef.current?.parentElement;
    scrollRootRef.current = el;
    if (!el) return;
    const onScroll = () => {
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
      if (nearBottom) setHasNewBelow(false);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const el = scrollRootRef.current;
    const prev = lastSeenCountRef.current;
    lastSeenCountRef.current = messages.length;
    if (!el || messages.length <= prev) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom) {
      listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    } else {
      setHasNewBelow(true);
    }
  }, [messages.length]);
  const jumpToBottom = () => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    setHasNewBelow(false);
  };

  const send = async () => {
    const txt = input.trim();
    if (!txt || busy) return;
    setBusy(true);
    try {
      if (editing) {
        const r = await editChatMessage(roomId, editing.message_id, { kind: "text", text: txt });
        bcEcho({ type: "bc_edit", message: r });
      } else {
        const body = { kind: "text", text: txt };
        if (replying) body.reply_to = { message_id: replying.message_id, author: replying.author_name, preview: (replying.body?.text || `[${replying.body?.kind || ""}]`).slice(0, 80) };
        const pendingId = addPendingMessage(body);
        try {
          const r = (thread.kind === "public")
            ? await postPublicChat(roomId, body)
            : await postChatMessage(roomId, thread.thread_id, body);
          resolvePendingMessage(pendingId, r);
          bcEcho({ type: "bc_new", message: r });
        } catch (err) {
          discardPendingMessage(pendingId);
          throw err;
        }
      }
      setInput(""); setEditing(null); setReplying(null); setShowEmoji(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  const postBody = async (body, optimisticBody = body) => {
    const pendingId = addPendingMessage(optimisticBody);
    try {
      const r = (thread.kind === "public")
        ? await postPublicChat(roomId, body)
        : await postChatMessage(roomId, thread.thread_id, body);
      resolvePendingMessage(pendingId, r);
      bcEcho({ type: "bc_new", message: r });
      return true;
    } catch (e) {
      discardPendingMessage(pendingId);
      toast.error(e?.response?.data?.detail || "Send failed");
      return false;
    }
  };

  const clearPendingAttachment = useCallback(() => {
    setPendingAttachment((current) => {
      if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
      return null;
    });
  }, []);

  const onPickAttachment = (file) => {
    if (!file) return;
    const mime = (file.type || "").toLowerCase();
    const ext = (file.name || "").split(".").pop()?.toLowerCase();
    const isGif = mime === "image/gif" || ext === "gif";
    const isImage = ["image/png", "image/jpeg"].includes(mime) || ["png", "jpg", "jpeg"].includes(ext);
    if (!isGif && !isImage) {
      toast.error("Choose a GIF, PNG, or JPG image.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Images must be 10MB or smaller.");
      return;
    }
    clearPendingAttachment();
    setPendingAttachment({
      file,
      kind: isGif ? "gif" : "image",
      previewUrl: URL.createObjectURL(file),
    });
  };

  const sendPendingAttachment = async () => {
    const attachment = pendingAttachment;
    if (!attachment || busy) return;
    setBusy(true);
    try {
      const meta = attachment.kind === "gif"
        ? await uploadChatGif(roomId, attachment.file)
        : await uploadChatFile(roomId, attachment.file);
      const body = {
        kind: attachment.kind,
        file_id: meta.file_id,
        url: meta.url,
        preview_url: meta.url,
        alt: meta.original_name,
        original_name: meta.original_name,
        mime: meta.mime,
        size_bytes: meta.size_bytes,
        expires_at: meta.expires_at,
      };
      if (replying) {
        body.reply_to = {
          message_id: replying.message_id,
          author: replying.author_name,
          preview: (replying.body?.text || `[${replying.body?.kind || "message"}]`).slice(0, 80),
        };
      }
      const optimisticBody = { ...body, url: attachment.previewUrl, preview_url: attachment.previewUrl };
      const sent = await postBody(body, optimisticBody);
      if (sent) { clearPendingAttachment(); setReplying(null); }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.error === "GIF_TOO_LARGE" || d.error === "FILE_TOO_LARGE")) ? "Images must be 10MB or smaller." : (typeof d === "string" ? d : "Image upload failed"));
    } finally { setBusy(false); }
  };

  const onDelete = async (m) => {
    try { await deleteChatMessage(roomId, m.message_id); bcEcho({ type: "bc_delete", message_id: m.message_id }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const onEditStart = (m) => { setEditing(m); setReplying(null); setInput(m.body?.text || ""); setTimeout(() => inputRef.current?.focus(), 50); };
  const onReplyStart = (m) => { setReplying(m); setEditing(null); setTimeout(() => inputRef.current?.focus(), 50); };
  const onReactMessage = async (m, emoji) => {
    try {
      const updated = await toggleChatMessageReaction(roomId, m.message_id, emoji);
      setMessages((prev) => prev.map((item) => item.message_id === updated.message_id ? updated : item));
      bcEcho({ type: "bc_reactions", message: updated });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add reaction");
    }
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto" data-testid="chat-message-list">
        {messages.length === 0 ? (
          <div className="text-center text-white/40 text-xs py-8" data-testid="chat-empty">No messages yet. Say hi 👋</div>
        ) : threadedView ? messageTree.map((m) => (
          <MessageBranch key={m.message_id} m={m} depth={0} myIdentity={myIdentity}
            canModerate={canModerate} onDelete={onDelete} onEdit={onEditStart} onReply={onReplyStart} onReact={onReactMessage} />
        )) : messages.map((m) => (
          <MessageRow key={m.message_id} m={m} myIdentity={myIdentity}
            canModerate={canModerate} onDelete={onDelete} onEdit={onEditStart} onReply={onReplyStart} onReact={onReactMessage} />
        ))}
        <div ref={listEndRef} />
      </div>
      <div className="border-t border-white/5 p-2 relative">
        {hasNewBelow && (
          <button onClick={jumpToBottom}
                  className="absolute -top-9 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#FF007F] text-white text-[11px] font-bold shadow-lg hover:bg-[#FF007F]/90 transition-colors"
                  data-testid="chat-new-below-pill">
            ↓ New messages
          </button>
        )}
        {/* Phase 3.1 — reply-to pill */}
        {replying && (
          <div className="mb-1 px-2 py-1.5 bg-white/[0.04] border-l-2 border-[#00F0FF]/70 rounded-r flex items-center justify-between gap-2" data-testid="chat-replying-pill">
            <div className="text-[11px] text-white/70 truncate">
              <span className="text-[#00F0FF]/80 font-semibold">↩ Replying to {replying.author_name}:</span>{" "}
              <span className="text-white/55">{(replying.body?.text || "").slice(0, 60)}</span>
            </div>
            <button onClick={() => setReplying(null)} className="text-white/40 hover:text-white" data-testid="chat-replying-clear"><X className="w-3 h-3" /></button>
          </div>
        )}
        {editing && (
          <div className="text-[10px] text-amber-300 mb-1 flex items-center gap-1" data-testid="chat-editing-banner">
            Editing… <button onClick={() => { setEditing(null); setInput(""); }} className="text-white/40 underline ml-1">cancel</button>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 mb-1 px-0.5">
          <span className="text-[9px] uppercase tracking-[0.12em] text-white/35">Conversation view</span>
          <div className="flex rounded border border-white/10 overflow-hidden" data-testid="chat-view-toggle">
            <button onClick={() => setConversationView(false)} className={`px-2 py-1 text-[10px] ${!threadedView ? "bg-white/15 text-white" : "text-white/45 hover:text-white"}`} data-testid="chat-view-flat">Standard</button>
            <button onClick={() => setConversationView(true)} className={`px-2 py-1 text-[10px] flex items-center gap-1 ${threadedView ? "bg-[#00F0FF]/20 text-[#00F0FF]" : "text-white/45 hover:text-white"}`} data-testid="chat-view-threaded"><ListTree className="w-3 h-3" /> Threaded</button>
          </div>
        </div>

        {/* Phase 3.1 — formatting toolbar */}
        <div className="flex items-center gap-0.5 mb-1.5 px-0.5" data-testid="chat-format-toolbar">
          <ToolbarBtn label="Bold (**text**)" testid="chat-format-bold" onClick={() => applyWrap("**")}>
            <Bold className="w-3.5 h-3.5" />
          </ToolbarBtn>
          <ToolbarBtn label="Italic (*text*)" testid="chat-format-italic" onClick={() => applyWrap("*")}>
            <Italic className="w-3.5 h-3.5" />
          </ToolbarBtn>
          <ToolbarBtn label="Strike (~~text~~)" testid="chat-format-strike" onClick={() => applyWrap("~~")}>
            <span className="text-[11px] line-through font-bold">S</span>
          </ToolbarBtn>
          <ToolbarBtn label="Code (`text`)" testid="chat-format-code" onClick={() => applyWrap("`")}>
            <Code className="w-3.5 h-3.5" />
          </ToolbarBtn>
          <ToolbarBtn label="Insert link" testid="chat-format-link" onClick={() => setLinkPopup({ text: "", url: "" })}>
            <LinkIcon className="w-3.5 h-3.5" />
          </ToolbarBtn>
          <div className="ml-auto text-[9px] text-white/30 font-mono">**bold**  *italic*  ~~strike~~  `code`</div>
        </div>
        {linkPopup && (
          <div className="absolute bottom-24 left-2 z-30 bg-[#0d0d14] border border-white/10 rounded-lg p-2 shadow-2xl w-[260px]" data-testid="chat-link-popup">
            <input autoFocus value={linkPopup.text} onChange={(e) => setLinkPopup({...linkPopup, text: e.target.value})} placeholder="Display text"
                   className="w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white placeholder-white/30 mb-1" data-testid="chat-link-text" />
            <input value={linkPopup.url} onChange={(e) => setLinkPopup({...linkPopup, url: e.target.value})} placeholder="https://…"
                   className="w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white placeholder-white/30" data-testid="chat-link-url" />
            <div className="flex gap-1 mt-1.5">
              <button onClick={() => setLinkPopup(null)} className="flex-1 px-2 py-1 text-[11px] rounded bg-white/5 hover:bg-white/10" data-testid="chat-link-cancel">Cancel</button>
              <button onClick={() => { if (linkPopup.url) insertLink(linkPopup.text, linkPopup.url); setLinkPopup(null); }}
                      className="flex-1 px-2 py-1 text-[11px] rounded bg-gradient-to-r from-[#FF007F] to-[#00F0FF] font-semibold" data-testid="chat-link-insert">Insert</button>
            </div>
          </div>
        )}
        {showEmoji && (
          <div className="absolute bottom-14 left-2 z-20"><EmojiPickerLazy onPick={(em) => setInput((s) => s + em)} onClose={() => setShowEmoji(false)} /></div>
        )}
        {pendingAttachment && (
          <div className="mb-2 rounded-lg border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-2 flex items-center gap-2" data-testid="chat-attachment-preview">
            <img src={pendingAttachment.previewUrl} alt="Selected attachment preview" className="h-16 w-16 rounded object-cover border border-white/10" data-testid="chat-attachment-preview-image" />
            <div className="min-w-0 flex-1">
              <div className="text-[12px] text-white/85 truncate">{pendingAttachment.file.name}</div>
              <div className="text-[10px] text-white/45">{pendingAttachment.kind === "gif" ? "GIF" : "Image"} · {Math.max(1, Math.round(pendingAttachment.file.size / 1024))} KB</div>
            </div>
            <button onClick={clearPendingAttachment} className="p-1.5 rounded hover:bg-white/10 text-white/55 hover:text-white" title="Remove attachment" data-testid="chat-attachment-remove"><X className="w-4 h-4" /></button>
          </div>
        )}
        <div className="flex items-end gap-1">
          <button onClick={() => setShowEmoji((v) => !v)} className="p-2 rounded hover:bg-white/5" title="Choose any emoji" data-testid="chat-emoji-toggle"><Smile className="w-4 h-4 text-white/60" /></button>
          <button onClick={() => attachmentInputRef.current?.click()} className="p-2 rounded hover:bg-white/5" title="Attach a GIF, PNG, or JPG image" data-testid="chat-attachment-btn"><ImagePlay className="w-4 h-4 text-[#FF007F]" /></button>
          <input ref={attachmentInputRef} type="file" accept=".gif,.png,.jpg,.jpeg,image/gif,image/png,image/jpeg" className="hidden"
                 onChange={(e) => { onPickAttachment(e.target.files?.[0]); e.target.value = ""; }} data-testid="chat-attachment-input" />
          <textarea
            ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); pendingAttachment ? sendPendingAttachment() : send(); } }}
            placeholder={editing ? "Edit your message…" : pendingAttachment ? "Press Send to share this image…" : "Message… (Shift+Enter for newline; **bold** *italic*)"}
            rows={1} maxLength={4000}
            className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-[13px] text-white placeholder-white/30 resize-none focus:outline-none focus:border-[#00F0FF]/40 max-h-[120px]"
            data-testid="chat-input"
          />
          <button onClick={pendingAttachment ? sendPendingAttachment : send} disabled={busy || (!pendingAttachment && !input.trim())} className="p-2 rounded bg-gradient-to-r from-[#FF007F] to-[#00F0FF] disabled:opacity-30 disabled:cursor-not-allowed" data-testid="chat-send-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Send className="w-4 h-4 text-white" />}
          </button>
        </div>
      </div>
    </>
  );
}

// ---------- thread list (DMs + Groups) ----------
function ThreadList({ threads, kind, myIdentity, participants, onOpen, onCreateGroup }) {
  const filtered = (threads || []).filter((t) => t.kind === kind).sort((a, b) => (b.last_message_at || "").localeCompare(a.last_message_at || ""));
  const nameFor = (ident) => {
    const p = (participants || []).find((x) => x.identity === ident);
    return p?.name || ident.slice(0, 8);
  };
  return (
    <div className="overflow-y-auto p-2 flex-1" data-testid={`chat-${kind}-list`}>
      {kind === "group" && (
        <button onClick={onCreateGroup}
                className="w-full mb-2 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-[#FF007F]/15 to-[#00F0FF]/15 border border-white/10 hover:border-white/30 text-xs font-semibold"
                data-testid="chat-new-group-btn">
          <Plus className="w-3.5 h-3.5" /> New group chat
        </button>
      )}
      {filtered.length === 0 ? (
        <div className="text-center text-white/40 text-xs py-8" data-testid={`chat-${kind}-empty`}>
          {kind === "dm" ? "No direct messages yet. Use 💬 Chat on a participant's tile." : "No groups yet. Create one above."}
        </div>
      ) : filtered.map((t) => {
        const otherMembers = (t.members || []).filter((m) => m !== myIdentity);
        const label = t.name || (t.kind === "dm" ? nameFor(otherMembers[0] || "") : otherMembers.slice(0, 3).map(nameFor).join(", "));
        return (
          <button key={t.thread_id} onClick={() => onOpen(t)}
                  className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-white/5 border border-transparent hover:border-white/10 mb-1"
                  data-testid={`chat-thread-row-${t.thread_id}`}>
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-semibold text-white/90 truncate flex items-center gap-1.5">
                {t.kind === "dm" ? <Mail className="w-3 h-3 text-[#00F0FF]" /> : <Users className="w-3 h-3 text-[#FF007F]" />}
                {label || "(unnamed)"}
              </span>
              <span className="text-[9px] text-white/30 font-mono shrink-0 ml-2">{t.last_message_at ? formatTime(t.last_message_at) : ""}</span>
            </div>
            {otherMembers.length > 1 && t.kind === "group" && (
              <div className="text-[10px] text-white/40 mt-0.5">{otherMembers.length + 1} members</div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------- new group modal ----------
function NewGroupModal({ participants, myIdentity, onCancel, onCreate }) {
  const [selected, setSelected] = useState(new Set());
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const eligible = (participants || []).filter((p) => p.identity && p.identity !== myIdentity);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const submit = async () => {
    if (selected.size < 2) return;
    setBusy(true);
    try { await onCreate(Array.from(selected), name.trim() || null); }
    finally { setBusy(false); }
  };
  return (
    <div className="absolute inset-0 z-30 bg-black/80 backdrop-blur grid place-items-center p-3" data-testid="chat-new-group-modal">
      <div className="bg-[#0d0d14] border border-white/10 rounded-xl p-4 w-full max-w-xs">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-base font-bold">New group chat</h3>
          <button onClick={onCancel} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name (optional)"
               maxLength={80}
               className="w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-[12px] text-white placeholder-white/30 mb-3 focus:outline-none focus:border-[#00F0FF]/40"
               data-testid="chat-new-group-name" />
        <div className="max-h-[240px] overflow-y-auto space-y-1 mb-3">
          {eligible.length === 0 ? (
            <div className="text-xs text-white/40 text-center py-4">No other participants in the room yet.</div>
          ) : eligible.map((p) => (
            <label key={p.identity} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer" data-testid={`chat-new-group-row-${p.identity}`}>
              <input type="checkbox" checked={selected.has(p.identity)} onChange={() => toggle(p.identity)}
                     className="w-4 h-4 accent-[#FF007F]" data-testid={`chat-new-group-check-${p.identity}`} />
              <span className="w-6 h-6 rounded-full grid place-items-center text-[10px] font-bold uppercase" style={{ background: avatarColor(p.identity) }}>{(p.name || "?").slice(0,1)}</span>
              <span className="text-[12px] text-white/90 truncate">{p.name}</span>
            </label>
          ))}
        </div>
        <p className="text-[10px] text-white/40 mb-2">Selected: {selected.size} · need ≥ 2</p>
        <div className="flex gap-2">
          <button onClick={onCancel} className="flex-1 px-3 py-2 text-[12px] rounded-lg bg-white/5 hover:bg-white/10 border border-white/10" data-testid="chat-new-group-cancel">Cancel</button>
          <button onClick={submit} disabled={selected.size < 2 || busy}
                  className="flex-1 px-3 py-2 text-[12px] rounded-lg bg-gradient-to-r from-[#FF007F] to-[#00F0FF] font-semibold disabled:opacity-30"
                  data-testid="chat-new-group-create">
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- main panel ----------
const ChatPanel = forwardRef(function ChatPanel({
  roomId, myIdentity, canModerate, registerHandler, participants, open, onClose, onPopout,
}, ref) {
  const [tab, setTab] = useState("public");
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null); // a thread object once opened
  const [mgmtVisible, setMgmtVisible] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  // R17-A — Per-tab unread counters. Computed from threads' last_message_at
  // vs. user's last_read_at (localStorage `sr-tab-read-{roomId}-{tab}`).
  // Recomputed whenever threads list refreshes.
  const [unreadByTab, setUnreadByTab] = useState({ public: 0, mgmt: 0, dm: 0, group: 0 });

  const readKey = useCallback((t) => `sr-tab-read-${roomId}-${t}`, [roomId]);

  const recomputeUnread = useCallback((threadList) => {
    const out = { public: 0, mgmt: 0, dm: 0, group: 0 };
    let getRead;
    try {
      getRead = (t) => Number(localStorage.getItem(readKey(t)) || 0);
    } catch {
      getRead = () => 0;
    }
    for (const th of threadList || []) {
      const lastIso = th.last_message_at;
      if (!lastIso) continue;
      const lastTs = new Date(lastIso).getTime();
      if (Number.isNaN(lastTs)) continue;
      const readTs = getRead(th.kind);
      if (lastTs > readTs) {
        out[th.kind] = (out[th.kind] || 0) + 1;
      }
    }
    setUnreadByTab(out);
  }, [readKey]);

  // Mark a tab as fully read NOW. Persist + clear local count for that tab.
  const markTabRead = useCallback((tabId) => {
    try { localStorage.setItem(readKey(tabId), String(Date.now())); } catch { /* */ }
    setUnreadByTab((p) => ({ ...p, [tabId]: 0 }));
  }, [readKey]);

  const switchTab = useCallback((next) => {
    setTab(next);
    setActiveThread(null);
    markTabRead(next);
  }, [markTabRead]);

  const reloadThreads = useCallback(async () => {
    try {
      const r = await listChatThreads(roomId);
      const list = r.threads || [];
      setThreads(list);
      setMgmtVisible(!!list.find((t) => t.kind === "mgmt"));
      recomputeUnread(list);
    } catch { /* */ }
  }, [roomId, recomputeUnread]);

  useEffect(() => { if (open) reloadThreads(); }, [open, reloadThreads]);

  // R17-A — Refresh threads (and therefore unread counts) every 10s while
  // the panel is open. Cheap poll, gives us "live" counters without
  // bolting onto every LiveKit data event.
  useEffect(() => {
    if (!open) return;
    const id = setInterval(reloadThreads, 10000);
    return () => clearInterval(id);
  }, [open, reloadThreads]);

  // R17-A — When the currently-active tab gains an update, mark it read
  // immediately (user is looking at it). Watching threads keeps this
  // honest even when the user is just hovering a different thread in the
  // same tab.
  useEffect(() => {
    if (!open) return;
    markTabRead(tab);
  }, [open, tab, threads, markTabRead]);

  // Listen for thread-created broadcasts + mgmt member changes so the sidebar stays fresh
  useEffect(() => {
    if (!registerHandler) return;
    const unsub = registerHandler((msg) => {
      if (msg?.type === "chat_thread_created" || msg?.type === "mgmt_members_changed") {
        reloadThreads();
      }
    });
    return unsub;
  }, [registerHandler, reloadThreads]);

  // When entering public tab, synthesize the activeThread from the threads list so the same ThreadView is reused.
  useEffect(() => {
    if (!open) return;
    if (tab === "public") {
      const pub = (threads || []).find((t) => t.kind === "public");
      if (pub) setActiveThread(pub);
    }
  }, [tab, threads, open]);

  // Public imperative API for Room.jsx
  useImperativeHandle(ref, () => ({
    openDmWith: async (identity, name) => {
      try {
        const t = await createDm(roomId, identity);
        await reloadThreads();
        setTab("dm"); setActiveThread(t);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Couldn't open DM with ${name || "user"}`);
      }
    },
    focusTab: (t) => { setTab(t); setActiveThread(null); },
  }), [roomId, reloadThreads]);

  const createGroupAndOpen = async (memberIdentities, name) => {
    try {
      const t = await createGroupChat(roomId, memberIdentities, name);
      await reloadThreads();
      setShowNewGroup(false); setTab("group"); setActiveThread(t);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create group"); }
  };

  if (!open) return null;

  const currentThreadForTab = (() => {
    if (tab === "public") return (threads || []).find((t) => t.kind === "public") || { thread_id: `public:${roomId}`, kind: "public", members: [] };
    if (tab === "mgmt") return (threads || []).find((t) => t.kind === "mgmt") || null;
    return activeThread && activeThread.kind === tab ? activeThread : null;
  })();

  return (
    <>
      {/* Phase B — mobile: tap-outside-to-close backdrop. Hidden on md+. */}
      <div className="md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="chat-panel-backdrop" />
      <aside className="md:w-[340px] md:shrink-0 md:h-full md:border-l md:border-white/10 md:relative md:inset-auto md:z-auto fixed inset-0 z-50 w-full h-[100dvh] bg-[#08080c] flex flex-col" data-testid="chat-panel">
      <header className="flex items-center gap-1 px-2 pt-2 pb-1 border-b border-white/5 overflow-x-auto">
        <TabBtn id="public" cur={tab} setCur={switchTab} icon="💬" label="Public" unread={unreadByTab.public} />
        {mgmtVisible && <TabBtn id="mgmt" cur={tab} setCur={switchTab} icon="🛡️" label="Mgmt" unread={unreadByTab.mgmt} />}
        <TabBtn id="dm" cur={tab} setCur={switchTab} icon="📩" label="DMs" unread={unreadByTab.dm} />
        <TabBtn id="group" cur={tab} setCur={switchTab} icon="👥" label="Groups" unread={unreadByTab.group} />
        <button onClick={() => {
                  if (onPopout) onPopout();
                  else window.open(`/r/${roomId}/chat-popout`, `chat-popout-${roomId}`, "width=420,height=720,resizable=yes");
                }}
                title="Pop out chat" className="ml-auto p-1.5 rounded hover:bg-white/5" data-testid="chat-popout-btn">
          <ExternalLink className="w-4 h-4 text-white/50" />
        </button>
        {/* R17-B — Explicit X close on the right rail. The mobile sheet
            already had a backdrop tap-to-close; this gives desktop hosts
            and mobile users a labeled affordance in the top-right. Tap
            target is intentionally larger (32×32) for touch ergonomics. */}
        <button
          onClick={onClose}
          className="ml-0.5 w-8 h-8 shrink-0 grid place-items-center rounded hover:bg-white/10 text-white/65 hover:text-white"
          title="Close chat"
          aria-label="Close chat"
          data-testid="chat-panel-close"
        >
          <X className="w-4 h-4" />
        </button>
      </header>

      {/* Tab body */}
      {tab === "public" && (currentThreadForTab
        ? <ThreadView roomId={roomId} thread={currentThreadForTab} myIdentity={myIdentity} canModerate={canModerate} registerHandler={registerHandler} />
        : <div className="text-center text-white/40 text-xs py-8">Loading…</div>)}

      {tab === "mgmt" && (currentThreadForTab
        ? <ThreadView roomId={roomId} thread={currentThreadForTab} myIdentity={myIdentity} canModerate={canModerate} registerHandler={registerHandler} />
        : <div className="text-center text-white/40 text-xs py-8">Loading…</div>)}

      {tab === "dm" && (
        activeThread && activeThread.kind === "dm"
          ? (
            <>
              <div className="flex items-center gap-2 px-3 py-1.5 border-b border-white/5 bg-white/[0.02]">
                <button onClick={() => setActiveThread(null)} className="p-1 hover:bg-white/5 rounded" data-testid="chat-back-btn"><ArrowLeft className="w-3.5 h-3.5 text-white/60" /></button>
                <span className="text-[12px] text-white/80 font-semibold truncate">{
                  (activeThread.members || []).filter((m) => m !== myIdentity).map((id) => (participants || []).find((p) => p.identity === id)?.name || id.slice(0,8)).join(", ")
                }</span>
              </div>
              <ThreadView roomId={roomId} thread={activeThread} myIdentity={myIdentity} canModerate={canModerate} registerHandler={registerHandler} />
            </>
          )
          : <ThreadList threads={threads} kind="dm" myIdentity={myIdentity} participants={participants} onOpen={setActiveThread} />
      )}

      {tab === "group" && (
        activeThread && activeThread.kind === "group"
          ? (
            <>
              <div className="flex items-center gap-2 px-3 py-1.5 border-b border-white/5 bg-white/[0.02]">
                <button onClick={() => setActiveThread(null)} className="p-1 hover:bg-white/5 rounded" data-testid="chat-back-btn"><ArrowLeft className="w-3.5 h-3.5 text-white/60" /></button>
                <span className="text-[12px] text-white/80 font-semibold truncate">{activeThread.name || `Group · ${(activeThread.members || []).length} members`}</span>
              </div>
              <ThreadView roomId={roomId} thread={activeThread} myIdentity={myIdentity} canModerate={canModerate} registerHandler={registerHandler} />
            </>
          )
          : <ThreadList threads={threads} kind="group" myIdentity={myIdentity} participants={participants} onOpen={setActiveThread} onCreateGroup={() => setShowNewGroup(true)} />
      )}

      {showNewGroup && (
        <NewGroupModal participants={participants} myIdentity={myIdentity}
                       onCancel={() => setShowNewGroup(false)} onCreate={createGroupAndOpen} />
      )}
    </aside>
    </>
  );
});

export default ChatPanel;

function TabBtn({ id, cur, setCur, icon, label, unread = 0 }) {
  const active = cur === id;
  return (
    <button onClick={() => setCur(id)}
            className={`relative px-2.5 py-1.5 rounded-md text-[11px] font-semibold flex items-center gap-1 ${active ? "bg-white/10 text-white" : "text-white/40 hover:text-white/80 hover:bg-white/5"}`}
            data-testid={`chat-tab-${id}`}>
      <span aria-hidden>{icon}</span> {label}
      {/* R17-A — Per-tab unread badge. Hidden when count is 0. */}
      {unread > 0 && (
        <span
          className="ml-0.5 min-w-[16px] h-[15px] rounded-full bg-[#FF007F] text-white text-[9px] font-bold grid place-items-center px-1 leading-none"
          data-testid={`chat-tab-${id}-badge`}
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </button>
  );
}

function ToolbarBtn({ children, label, onClick, testid }) {
  return (
    <button onClick={onClick} title={label} className="w-7 h-7 grid place-items-center rounded hover:bg-white/10 text-white/60 hover:text-white/90"
            data-testid={testid}>
      {children}
    </button>
  );
}

export function ChatTrigger({ open, setOpen, unread }) {
  return (
    <button onClick={() => setOpen((v) => !v)}
            className={`relative px-3 py-2 rounded-lg border text-xs font-semibold flex items-center gap-1.5 transition-colors ${open ? "bg-white/10 border-white/20" : "bg-white/5 border-white/10 hover:bg-white/10"}`}
            data-testid="chat-trigger-btn">
      <MessageCircle className="w-4 h-4 text-[#00F0FF]" />
      Chat
      {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] rounded-full bg-[#FF007F] text-white text-[9px] font-bold grid place-items-center px-1" data-testid="chat-unread-badge">{unread > 99 ? "99+" : unread}</span>}
    </button>
  );
}

// Per-tile emoji burst — popover with 12 quick-reacts. Returns picked emoji via callback.
export function TileReactPicker({ onPick, onClose }) {
  return (
    <div className="absolute z-30 bottom-10 left-1/2 -translate-x-1/2 bg-black/95 border border-white/15 rounded-xl p-2 shadow-2xl"
         data-testid="tile-react-picker"
         onClick={(e) => e.stopPropagation()}>
      <div className="grid grid-cols-6 gap-1">
        {QUICK_EMOJIS.map((e) => (
          <button key={e} onClick={() => onPick(e)}
                  className="w-8 h-8 grid place-items-center text-lg rounded hover:bg-white/10"
                  data-testid={`tile-react-${e}`}>
            {e}
          </button>
        ))}
      </div>
      <button onClick={onClose} className="text-[10px] text-white/40 mt-1 w-full text-center" data-testid="tile-react-close">close</button>
    </div>
  );
}
